import React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";

import { RemoteSelect } from "@/components/fields/remote-select";
import { useGeodataRepository } from "@/pages/geodata/hooks/use-geodata-repository";
import { useVenueRepository } from "@/pages/venues/hooks/use-venue-repository";
import {
  VenueNestedType,
  venueNestedSchema,
} from "@/pages/venues/schemas/venue.nested.form";

export function VenueSelectField({ id, inputProps }: AutoFormFieldProps) {
  const form = useFormContext();
  const value = useWatch({ name: id });
  const mapsUrl = useWatch({ name: "mapsUrl" });
  const venueTba = useWatch({ name: "venueTba" });
  const { search: searchVenues } = useVenueRepository();
  const { search: searchGeodata } = useGeodataRepository();

  return (
    <RemoteSelect<VenueNestedType>
      value={value}
      triggerType="change"
      disabled={!!mapsUrl || !!venueTba || inputProps?.disabled}
      onValueChanged={(selected) => {
        form.setValue(id, selected, { shouldDirty: true });
        // Only a real selection carries a location and a timezone. Clearing the venue must
        // leave them alone: both are REQUIRED, so writing `undefined` over them blocked submit
        // until the curator retyped what removing the venue had just taken away. Picking a
        // venue still overwrites both with the venue's own.
        if (selected?.geolocation) {
          form.setValue("geolocation", selected.geolocation, {
            shouldDirty: true,
          });
        }
        if (selected?.timezone) {
          form.setValue("timezone", selected.timezone, { shouldDirty: true });
        }
        if (selected?.geodata?.name) {
          searchGeodata(selected?.geodata.name).then((geodata) => {
            console.debug("Geodata search results for venue geodata:", geodata);
            const bestMatch = geodata[0];
            if (bestMatch) {
              form.setValue("geodata", bestMatch, { shouldDirty: true });
            }
          });
        }
      }}
      showSelected
      accessorFn={(option) => ({
        id: option?.id || "",
        name: option ? `${option?.name} (${option?.address})` : "",
        iconUrl: option?.media?.photos[0]?.url,
      })}
      onSearch={async (query, onStatusChanged) => {
        onStatusChanged("loading");
        const results = await searchVenues(query);
        console.debug("Venue search results:", results);
        onStatusChanged("success");
        return results.map((item) => venueNestedSchema.parse(item));
      }}
    />
  );
}
